import { useMemo, type ReactNode } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";

import {
  defaultFeatureFlags,
  FeatureFlagsContext,
} from "./feature-flags-context";
import {
  LD_IMPORTED_CREDENTIALS_SUPPORT_FLAG,
  LD_IMPORTED_SANDBOX_CREDENTIALS_FLAG,
  LD_LIVE_CREDENTIALS_FLAG,
} from "./flag-keys";
import { useLaunchDarklyFlagsResolved } from "./useLaunchDarklyFlagsResolved";

function LaunchDarklyFeatureFlagsBridge({ children }: { children: ReactNode }) {
  const flags = useFlags();
  const flagsResolved = useLaunchDarklyFlagsResolved();
  const value = useMemo(
    () => ({
      // Fail-closed: only true when LD explicitly returns true.
      liveCredentialsEnabled: flags[LD_LIVE_CREDENTIALS_FLAG] === true,
      importedSandboxCredentialsEnabled:
        flags[LD_IMPORTED_SANDBOX_CREDENTIALS_FLAG] === true,
      importedCredentialsSupportEnabled:
        flags[LD_IMPORTED_CREDENTIALS_SUPPORT_FLAG] === true,
      flagsResolved,
    }),
    [flags, flagsResolved],
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

function DefaultFeatureFlags({ children }: { children: ReactNode }) {
  return (
    <FeatureFlagsContext.Provider value={defaultFeatureFlags}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

interface FeatureFlagsProviderProps {
  children: ReactNode;
  launchDarklyEnabled: boolean;
}

export function FeatureFlagsProvider({
  children,
  launchDarklyEnabled,
}: FeatureFlagsProviderProps) {
  if (launchDarklyEnabled) {
    return (
      <LaunchDarklyFeatureFlagsBridge>
        {children}
      </LaunchDarklyFeatureFlagsBridge>
    );
  }

  return <DefaultFeatureFlags>{children}</DefaultFeatureFlags>;
}
